import React from 'react';
import { fr } from '../i18n/fr';

export interface BufferingOverlayProps {
  isBuffering: boolean;
  isTimedOut: boolean;
  remainingSeconds: number;
  errorMessage?: string;
  onRetry: () => void;
}

/**
 * Buffering overlay with 30s reconnection timeout (Req 1.6).
 * Shows buffering spinner during reconnection attempts.
 * Shows French error message with retry button on timeout (Req 1.8).
 */
export const BufferingOverlay: React.FC<BufferingOverlayProps> = ({
  isBuffering,
  isTimedOut,
  remainingSeconds,
  errorMessage,
  onRetry,
}) => {
  if (!isBuffering && !isTimedOut && !errorMessage) return null;

  return (
    <div style={styles.overlay} role="alert" aria-live="assertive">
      {isTimedOut || errorMessage ? (
        <div style={styles.errorContainer}>
          <ErrorIcon />
          <p style={styles.errorText}>
            {errorMessage
              ? fr.errors[errorMessage as keyof typeof fr.errors] || fr.errors.fatalError
              : fr.errors.timeout}
          </p>
          <button onClick={onRetry} style={styles.retryButton}>
            {fr.controls.retry}
          </button>
        </div>
      ) : (
        <div style={styles.bufferingContainer}>
          <Spinner />
          <p style={styles.bufferingText}>{fr.status.reconnecting}</p>
          <p style={styles.countdown}>{remainingSeconds}s</p>
        </div>
      )}
    </div>
  );
};

const Spinner = () => (
  <div style={styles.spinner}>
    <svg width="48" height="48" viewBox="0 0 48 48">
      <circle
        cx="24"
        cy="24"
        r="20"
        fill="none"
        stroke="#006B2B"
        strokeWidth="4"
        strokeDasharray="80 40"
        strokeLinecap="round"
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 24 24"
          to="360 24 24"
          dur="1s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  </div>
);

const ErrorIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="#ff4444">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
  </svg>
);

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    zIndex: 20,
  },
  bufferingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  bufferingText: {
    color: '#ffffff',
    fontSize: '0.9rem',
    fontWeight: 500,
  },
  countdown: {
    color: '#b0b0b0',
    fontSize: '0.8rem',
  },
  spinner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '24px',
    maxWidth: '320px',
    textAlign: 'center',
  },
  errorText: {
    color: '#ffffff',
    fontSize: '0.9rem',
    lineHeight: 1.5,
  },
  retryButton: {
    padding: '10px 24px',
    borderRadius: '6px',
    backgroundColor: '#006B2B',
    color: '#ffffff',
    fontSize: '0.875rem',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
};
